import { Head, Link, router, usePage } from "@inertiajs/react"
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import DownloadIcon from "@mui/icons-material/Download"
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DateCalendar, DateField, DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/es"
import { useEffect, useRef, useState } from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import OrderPickerDialog, { type IOrderOption } from "@/components/OrderPickerDialog"

// ─── Types ───────────────────────────────────────────────────────────────────

interface IExtrusion {
    id?: number
    date: string
    code: string
    version: number
    orders?: IOrderOption[]
}

const EMPTY_FORM = (): IExtrusion => ({ date: dayjs().format("YYYY-MM-DD"), code: "", version: 1, orders: [] })

const PAPER_COLOR_KEYS: Record<number, string> = { 0: "na", 1: "white", 2: "kraft" }


const formatOrderCode = (code: string) =>
    code.replace(/^OF-ST(\d+)-/, (_, m) => `OFT-ST${m.padStart(2, "0")}-`)

type Props = {
    is_edit: boolean
    extrusion: IExtrusion | null
    back_params: Record<string, string | null>
    errors?: Record<string, string[]>
}

// ─── Orders section ───────────────────────────────────────────────────────────

function OrdersSection({ selectedOrders, onChange }: {
    selectedOrders: IOrderOption[]
    onChange: (value: IOrderOption[] | ((prev: IOrderOption[]) => IOrderOption[])) => void
}) {
    const { t, i18n } = useTranslation()
    const [pickerOpen, setPickerOpen] = useState(false)

    const [editingId, setEditingId] = useState<number | null>(null)
    const [draftDate, setDraftDate] = useState<Dayjs | null>(null)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Bumped by cancelEdit; commitDate captures a token at start and bails
    // post-await if it changed, so a stale response can't stomp a new editor.
    const commitTokenRef = useRef(0)

    const sortOrders = (orders: IOrderOption[]) =>
        [...orders].sort((a, b) => a.order_code.localeCompare(b.order_code))

    const handleAdd = (newOrders: IOrderOption[]) => {
        onChange(prev => sortOrders([...prev, ...newOrders]))
    }

    const cancelEdit = () => {
        commitTokenRef.current++
        setEditingId(null)
        setDraftDate(null)
        setAnchorEl(null)
        setSaving(false)
        setError(null)
    }

    const handleRemove = (id: number) => {
        onChange(prev => prev.filter(o => o.id !== id))
        if (editingId === id) cancelEdit()
    }

    const csrfToken = () =>
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''

    const saveEntryDate = async (id: number, date: string | null): Promise<string | null> => {
        const res = await fetch(`/orders/${id}/entry_date`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': csrfToken(),
            },
            body: JSON.stringify({ order: { order_entry_date: date } }),
        })
        if (!res.ok) {
            const body = await res.json().catch(() => ({}))
            const msg = translateErrorMessage(t, body?.errors?.order_entry_date)
            throw new Error(msg || `HTTP ${res.status}`)
        }
        const body = await res.json()
        return (body.order_entry_date as string | null) ?? null
    }

    const startEdit = (order: IOrderOption, anchor: HTMLElement) => {
        if (saving) return
        if (editingId === order.id) return
        setEditingId(order.id)
        setDraftDate(order.order_entry_date ? dayjs(order.order_entry_date) : null)
        setAnchorEl(anchor)
        setError(null)
    }

    const commitDate = async (newDate: string | null) => {
        if (editingId == null) return

        const editing = selectedOrders.find(o => o.id === editingId)
        if (!editing) { cancelEdit(); return }

        const previous = editing.order_entry_date ?? null
        if (newDate === previous) { cancelEdit(); return }

        const myToken = ++commitTokenRef.current
        setSaving(true)
        setError(null)
        try {
            const confirmed = await saveEntryDate(editing.id, newDate)
            if (commitTokenRef.current !== myToken) return
            onChange(prev => prev.map(o =>
                o.id === editing.id ? { ...o, order_entry_date: confirmed } : o
            ))
            setEditingId(null)
            setDraftDate(null)
            setAnchorEl(null)
            setSaving(false)
        } catch (err) {
            if (commitTokenRef.current !== myToken) return
            setError((err as Error).message)
            setSaving(false)
        }
    }

    const commitDraft = () => {
        if (draftDate && !draftDate.isValid()) return
        commitDate(draftDate ? draftDate.format("YYYY-MM-DD") : null)
    }

    const commitClear = () => commitDate(null)

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                    {t("extrusion.form.ordersSection")}
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setPickerOpen(true)}
                >
                    {t("extrusion.form.addOrders")}
                </Button>
            </Box>

            {selectedOrders.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 2 }}>
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {[
                                        t("orders.project"),
                                        t("orders.client"),
                                        t("orders.code"),
                                        t("orders.form.location"),
                                        t("extrusion.form.reference"),
                                        t("extrusion.form.materialColor"),
                                        t("extrusion.form.kilos"),
                                        t("extrusion.form.units"),
                                        t("orders.color"),
                                        t("extrusion.form.entryDate"),
                                        "",
                                    ].map((label, i) => (
                                        <TableCell key={i} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedOrders.map(order => (
                                    <TableRow key={order.id} hover>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {order.project_name}
                                                {order.status === "inactive" && (
                                                    <Chip label={t("orders.statuses.inactive")} size="small" color="warning" variant="outlined" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{order.client_name}</TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatOrderCode(order.order_code)}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{order.location || "—"}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {order.reference_name
                                                ? `${order.reference_name}${order.reference_width != null ? ` ${order.reference_width} x ${order.reference_height}` : ""}`
                                                : "—"}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{order.reference_paper_color != null ? t(`orders.form.color.${PAPER_COLOR_KEYS[order.reference_paper_color] ?? "na"}`) : "—"}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{order.general_info_programmed_total != null ? order.general_info_programmed_total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{order.general_info_requested_amount != null ? order.general_info_requested_amount.toLocaleString("en-US") : "—"}</TableCell>
                                        <TableCell align="center">{order.printing_inks_amount ?? "—"}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            <Box
                                                onClick={e => startEdit(order, e.currentTarget)}
                                                sx={{
                                                    cursor: "pointer",
                                                    textTransform: "capitalize",
                                                    px: 1, py: 0.5,
                                                    borderRadius: 1,
                                                    "&:hover": { bgcolor: "action.hover" },
                                                }}
                                            >
                                                {order.order_entry_date
                                                    ? dayjs(order.order_entry_date).locale(i18n.language).format("MMMM")
                                                    : "—"}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={t("common.remove")}>
                                                <IconButton size="small" color="error" onClick={() => handleRemove(order.id)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow sx={{ "& td": { fontWeight: "bold", borderTop: 2, borderColor: "divider" } }}>
                                    <TableCell>{t("common.total")}</TableCell>
                                    <TableCell colSpan={4} />
                                    <TableCell />
                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {selectedOrders.reduce((sum, o) => sum + (o.general_info_programmed_total ?? 0), 0)
                                            .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell colSpan={4} />
                                </TableRow>
                                {[
                                    { colorKey: "white", colorValue: 1 },
                                    { colorKey: "kraft",  colorValue: 2 },
                                ].map(({ colorKey, colorValue }) => {
                                    const kgs = selectedOrders
                                        .filter(o => o.reference_paper_color === colorValue)
                                        .reduce((sum, o) => sum + (o.general_info_programmed_total ?? 0), 0)
                                    return (
                                        <TableRow key={colorKey} sx={{ "& td": { color: "text.secondary", fontSize: "0.8rem", borderBottom: 0 } }}>
                                            <TableCell colSpan={5} />
                                            <TableCell>{t(`orders.form.color.${colorKey}`)}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                {kgs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell colSpan={4} />
                                        </TableRow>
                                    )
                                })}
                            </TableFooter>
                        </Table>
                    </Box>
                </Paper>
            )}

            {selectedOrders.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("extrusion.form.noOrders")}
                </Typography>
            )}

            <Popover
                open={editingId !== null && anchorEl !== null}
                anchorEl={anchorEl}
                onClose={() => { if (!saving) cancelEdit() }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <Box sx={{ p: 1, pt: 2 }}>
                    <DateField
                        autoFocus
                        label={t("extrusion.form.entryDate")}
                        value={draftDate}
                        onChange={val => setDraftDate(val)}
                        format="DD-MM-YYYY"
                        size="small"
                        disabled={saving}
                        sx={{ width: 284, display: "block", ml: "24px", mr: "12px" }}
                    />
                    <DateCalendar
                        value={draftDate}
                        onChange={val => setDraftDate(val)}
                        disabled={saving}
                    />
                    {error && (
                        <Typography variant="caption" color="error" sx={{ display: "block", px: 1, pb: 1 }}>
                            {error}
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.5, px: 1, pb: 1 }}>
                        <Button
                            size="small"
                            variant="text"
                            onClick={commitClear}
                            disabled={saving}
                            sx={{ color: "text.secondary", textTransform: "none", fontSize: "0.75rem", minWidth: 0 }}
                        >
                            {t("common.clear")}
                        </Button>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title={t("common.cancel")}>
                                <span>
                                    <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={t("common.save")}>
                                <span>
                                    <IconButton size="small" color="primary" onClick={commitDraft} disabled={saving}>
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Popover>

            <OrderPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onConfirm={handleAdd}
                excludedOrderIds={selectedOrders.map(o => o.id)}
                multiple
            />
        </Box>
    )
}

// ─── Main form ────────────────────────────────────────────────────────────────

export default function ExtrusionForm() {
    const { is_edit, extrusion, back_params, errors: serverErrors } = usePage<Props>().props
    const { t } = useTranslation()

    const backQueryString = new URLSearchParams(back_params as Record<string, string>).toString()
    const backUrl = `/extrusions?${backQueryString}`

    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})

    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) setLocalErrors(serverErrors)
    }, [serverErrors])

    const sortOrders = (orders: IOrderOption[]) =>
        [...orders].sort((a, b) => a.order_code.localeCompare(b.order_code))

    const [selectedOrders, setSelectedOrders] = useState<IOrderOption[]>(sortOrders(extrusion?.orders ?? []))

    const { control, handleSubmit, reset, getValues } = useForm<IExtrusion>({
        defaultValues: extrusion ?? EMPTY_FORM(),
    })

    // Reset form when server sends updated data (after save-and-continue)
    useEffect(() => {
        if (extrusion) {
            reset(extrusion)
            setSelectedOrders(sortOrders(extrusion.orders ?? []))
        }
    }, [extrusion])

    const buildPayload = (data: IExtrusion) => {
        const { orders: _orders, ...rest } = data
        return { extrusion: { ...rest, order_ids: selectedOrders.map(o => o.id) } }
    }

    const buildOnError = (originalUrl: string) =>
        (errs: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(Object.fromEntries(
                Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
            ))
        }

    const handleSave: SubmitHandler<IExtrusion> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const url = is_edit
            ? `/extrusions/${data.id}?${backQueryString}`
            : `/extrusions?${backQueryString}`
        const method = is_edit ? "patch" : "post"
        router[method](url, buildPayload(data), {
            onError: buildOnError(originalUrl),
            onFinish: () => setSubmitting(false),
        })
    }

    const handleSaveAndContinue: SubmitHandler<IExtrusion> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        if (is_edit) {
            router.patch(`/extrusions/${data.id}?${backQueryString}`, { ...buildPayload(data), stay: true }, {
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        } else {
            router.post(`/extrusions?${backQueryString}`, { ...buildPayload(data), stay: true }, {
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        }
    }

    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState(false)

    const handleDownloadExcel = async () => {
        const data = getValues()
        const payload = buildPayload(data)
        const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
        setDownloading(true)
        setDownloadError(false)
        try {
            const res = await fetch('/extrusions/export_excel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                body: JSON.stringify(payload),
            })
            if (!res.ok) {
                setDownloadError(true)
                return
            }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const codePart = data.code?.trim() || 'NUEVO RESUMEN ORDENES PRODUCCION'
            a.download = `${codePart}-V${String(data.version).padStart(2, '0')}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            setDownloadError(true)
        } finally {
            setDownloading(false)
        }
    }

    const title = is_edit
        ? (extrusion?.code ?? t("extrusion.form.editTitle"))
        : t("extrusion.form.newTitle")

    return (
        <>
            <Head title={title} />

            <Box sx={{
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
            }}>
                <Container maxWidth="xl">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <Tooltip title={t("common.back")}>
                            <IconButton LinkComponent={Link} href={backUrl} size="small">
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                        <Typography variant="subtitle1" noWrap
                            sx={{ flexGrow: 1, display: { xs: "none", sm: "block" }, fontWeight: "bold" }}>
                            {title}
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: "block", sm: "none" } }} />
                        <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadExcel}
                            disabled={submitting || downloading}
                        >
                            {downloading ? t("common.loading") : t("common.excel")}
                        </Button>
                        <Button color="inherit" LinkComponent={Link} href={backUrl} size="small" disabled={submitting}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleSubmit(handleSaveAndContinue)} disabled={submitting}>
                            {t("common.saveAndContinue")}
                        </Button>
                        <Button variant="contained" size="small" onClick={handleSubmit(handleSave)} disabled={submitting}>
                            {submitting ? t("common.saving") : t("common.save")}
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                {Object.keys(localErrors).length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {formatErrorMessages(t, localErrors).join(" · ")}
                    </Alert>
                )}

                {downloadError && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError(false)}>
                        {t("common.downloadError")}
                    </Alert>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                    <Controller
                        control={control}
                        name="code"
                        rules={{ required: t("extrusion.form.codeRequired") }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label={t("extrusion.form.code")}
                                size="small"
                                fullWidth
                                error={!!fieldState.error || !!localErrors.code}
                                helperText={fieldState.error?.message ?? translateErrorMessage(t, localErrors.code)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="date"
                        render={({ field }) => (
                            <DatePicker
                                label={t("extrusion.form.date")}
                                value={field.value ? dayjs(field.value) : null}
                                onChange={val => field.onChange(val?.format("YYYY-MM-DD") ?? "")}
                                format="DD-MM-YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        error: !!localErrors.date,
                                        helperText: translateErrorMessage(t, localErrors.date),
                                    },
                                }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="version"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("extrusion.form.version")}
                                type="number"
                                size="small"
                                fullWidth
                                slotProps={{ htmlInput: { min: 1 } }}
                                onChange={e => field.onChange(Number(e.target.value))}
                                error={!!localErrors.version}
                                helperText={translateErrorMessage(t, localErrors.version)}
                            />
                        )}
                    />
                </Box>

                <OrdersSection selectedOrders={selectedOrders} onChange={setSelectedOrders} />
            </Container>
        </>
    )
}
